import React, { useState, useEffect } from 'react';
import clinicbookingService from '../../../../../services/clinicbooking.service';
import { toast } from 'react-toastify';
import Loading from '../../../../common/Loading';
import DayHours from './DayHours';
import Swal from '../../../../helpers/swal';

function DoctorHours({ id, clinicid }) {
    const [hours, setHours] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);

    useEffect(() => {
        getDoctorHours();
    }, [id, clinicid]);

    const getDoctorHours = () => {
        setIsLoading(true);
        clinicbookingService
            .getDoctorHours(clinicid, id)
            .then((res) => {
                setHours(res.data);
            })
            .catch((err) => {
                console.log(err);
                setHours([]);
                toast.error('Failed to load doctor hours');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleUpdateHour = (updatedHour) => {
        setIsSaving(true);
        clinicbookingService
            .updateDoctorHours(clinicid, id, updatedHour)
            .then(() => {
                setHours((prevHours) => prevHours.map((hour) => (hour.id === updatedHour.id ? updatedHour : hour)));
                getDoctorHours();
            })
            .catch((err) => {
                console.log(err);
                toast.error('Failed to update hours');
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handleSyncClinicHours = () => {
        Swal.fire({
            title: 'Sync Clinic Hours?',
            text: "This will overwrite this doctor's weekly schedule with the clinic's current hours. Lunch breaks will be cleared.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-arrow-repeat"></i> Yes, sync hours`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-primary btn-sm me-2',
                cancelButton: 'btn btn-secondary btn-sm me-2',
            },
            buttonsStyling: false,
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }

            setIsSyncing(true);
            clinicbookingService
                .syncDoctorHoursFromClinic(clinicid, id)
                .then((res) => {
                    setHours(res.data || []);
                    toast.success('Doctor weekly schedule synced from clinic hours');
                })
                .catch((err) => {
                    console.log(err);
                    const message =
                        err?.response?.data?.message ||
                        err?.response?.data?.error ||
                        'Failed to sync clinic hours';
                    toast.error(message);
                })
                .finally(() => {
                    setIsSyncing(false);
                });
        });
    };

    const workingDays = hours.filter((hour) => hour.isOff === false).length;
    const offDays = hours.length - workingDays;

    return (
        <div className="notifications-panel">
            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-calendar-week"></i>
                    Weekly Schedule
                </span>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="small text-muted fw-normal">24-hour clock</span>
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleSyncClinicHours}
                        disabled={isLoading || isSaving || isSyncing}
                        title="Load current clinic hours into this doctor's weekly schedule"
                    >
                        <i className={`bi bi-arrow-repeat me-1 ${isSyncing ? 'notifications-spin' : ''}`}></i>
                        {isSyncing ? 'Syncing…' : 'Sync Clinic Hours'}
                    </button>
                </div>
            </div>

            <div className="notifications-panel__body">
                {isLoading ? (
                    <Loading />
                ) : (
                    <>
                        {hours.length > 0 && (
                            <div className="clinic-booking-hours-toolbar">
                                <div className="d-flex flex-wrap gap-2">
                                    <span className="clinic-booking-stat clinic-booking-stat--active">{workingDays} working</span>
                                    <span className="clinic-booking-stat clinic-booking-stat--inactive">{offDays} off</span>
                                    <span className="clinic-booking-stat clinic-booking-stat--total">{hours.length} days</span>
                                </div>
                            </div>
                        )}

                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle mb-0 clinic-booking-hours-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <i className="bi bi-calendar-day"></i> Day
                                        </th>
                                        <th>
                                            <i className="bi bi-toggle-on"></i> Status
                                        </th>
                                        <th>
                                            <i className="bi bi-play-circle"></i> Start
                                        </th>
                                        <th>
                                            <i className="bi bi-stop-circle"></i> End
                                        </th>
                                        <th>
                                            <i className="bi bi-egg-fried"></i> Lunch From
                                        </th>
                                        <th>
                                            <i className="bi bi-egg-fried"></i> Lunch To
                                        </th>
                                        <th className="text-center">
                                            <i className="bi bi-gear"></i> Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {hours.length > 0 ? (
                                        hours.map((hour) => (
                                            <DayHours key={hour.id} hours={hour} handleUpdate={handleUpdateHour} />
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7">
                                                <div className="clinic-booking-hours-empty">
                                                    <i className="bi bi-clock-history"></i>
                                                    No hours configured for this doctor yet. Use Sync Clinic Hours to load them.
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {(isSaving || isSyncing) && (
                            <div className="notifications-alert-banner mt-3 mb-0">
                                <i className="bi bi-arrow-repeat notifications-spin"></i>
                                <span>{isSyncing ? 'Syncing clinic hours…' : 'Saving changes…'}</span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default DoctorHours;
